import { useEffect, useRef } from 'react';
import { ConnectionQuality, type TrackPublication } from 'livekit-client';

import { Icon } from './Icon';
import { SpeakingMeter } from './SpeakingMeter';
import { initials, tileCaptions } from '../lib/format';
import { QUALITY_LABEL, type ParticipantView } from '../lib/livekit';
import './VideoTile.css';

/** Привязывает дорожку LiveKit к DOM-элементу и отвязывает при смене. */
function useTrackAttachment(
  publication: TrackPublication | undefined,
  ref: React.RefObject<HTMLMediaElement | null>,
) {
  const track = publication?.track;

  useEffect(() => {
    const element = ref.current;
    if (!element || !track) return;

    track.attach(element as HTMLMediaElement);
    return () => {
      track.detach(element as HTMLMediaElement);
    };
  }, [track, ref]);
}

export function VideoTile({
  participant,
  source = 'camera',
  featured = false,
  onActivate,
  pinned = false,
}: {
  participant: ParticipantView;
  source?: 'camera' | 'screen';
  featured?: boolean;
  /** Клик по плитке. Если не задан, плитка не интерактивна. */
  onActivate?: () => void;
  /** Плитка уже развёрнута — клик её свернёт. */
  pinned?: boolean;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);

  const publication =
    source === 'screen' ? participant.screenPublication : participant.cameraPublication;
  useTrackAttachment(publication, videoRef);

  const isScreen = source === 'screen';
  const hasVideo = isScreen ? participant.screenShareEnabled : participant.cameraEnabled;
  const poor =
    participant.connectionQuality === ConnectionQuality.Poor ||
    participant.connectionQuality === ConnectionQuality.Lost;

  const classes = [
    'tile',
    featured ? 'tile--featured' : '',
    isScreen ? 'tile--screen' : '',
    // Рамка говорящего — SKY, холодный акцент для веб-среды.
    participant.isSpeaking && !isScreen ? 'tile--speaking' : '',
    onActivate ? 'tile--clickable' : '',
  ]
    .filter(Boolean)
    .join(' ');

  // Плитка становится кнопкой, только когда по ней есть что нажать:
  // иначе экранный диктор объявлял бы каждое видео как элемент управления.
  const Root = onActivate ? 'button' : 'div';
  const interaction = onActivate
    ? ({
        type: 'button' as const,
        onClick: onActivate,
        'aria-pressed': pinned,
        title: pinned ? 'Свернуть' : `${participant.name} — на весь экран`,
      } as const)
    : {};

  // Экранная подпись и подпись для файла записи различаются — правило
  // и причина в `lib/format.ts`. Записывающий модуль читает атрибут,
  // а не разбирает вёрстку.
  const caption = tileCaptions(participant.name, { isLocal: participant.isLocal, isScreen });

  return (
    <Root className={classes} data-record-name={caption.record} {...interaction}>
      <video
        ref={videoRef}
        className={`tile__video ${hasVideo ? '' : 'tile__video--hidden'} ${
          participant.isLocal && !isScreen ? 'tile__video--mirrored' : ''
        }`}
        autoPlay
        playsInline
        // Свой звук не воспроизводим: иначе будет эхо.
        muted={participant.isLocal || isScreen}
      />

      {!hasVideo && (
        <div className="tile__placeholder" aria-hidden>
          <span className="tile__avatar">{initials(participant.name)}</span>
        </div>
      )}

      {/* Ярлык с именем примыкает к нижнему краю плитки. */}
      <div className="tile__label">
        <span className="yarlyk yarlyk--bottom yarlyk--ghost tile__name">
          {!isScreen && (
            <SpeakingMeter
              participant={participant.raw}
              muted={!participant.micEnabled}
              size={17}
            />
          )}
          <span className="tile__name-text">{caption.screen}</span>
        </span>
      </div>

      {poor && !isScreen && (
        <span
          className="tile__quality"
          title={QUALITY_LABEL[participant.connectionQuality]}
          aria-label={QUALITY_LABEL[participant.connectionQuality]}
        >
          <Icon name="alert" size={14} />
        </span>
      )}

      {onActivate && (
        <span className="tile__zoom" aria-hidden>
          <Icon name={pinned ? 'minimize' : 'maximize'} size={15} />
        </span>
      )}
    </Root>
  );
}
